import type {
  RenderCaption,
  RenderClip,
  RenderEffect,
  RenderJob,
  RenderSource,
  RenderTimeline,
  RenderTrack,
  RenderTransform
} from '../../../../core/render-runtime/worker/render-job.model';
import { fontSpecsForCaptions } from '../../../../core/render-runtime/worker/render-font-assets';
import type {
  ReviewBrowserFeatureLayer,
  ReviewBrowserMediaTimelineClip,
  ReviewBrowserRenderCaptionOptions
} from '../../models/review-browser-render.model';
import type {
  ReviewOverlayLayerSelection,
  ReviewSoundtrackSelection
} from '../../models/review-template-options.model';
import type { ReviewCaptionSegment } from '../../models/review-video.model';

/**
 * processing-render-job.adapter — pure mapping from the "Processing / review
 * upload" render inputs (the options the legacy
 * `ReviewVideoRenderingService.renderReviewVideo` builds) to the unified,
 * serializable `RenderJob` contract consumed by the background render worker.
 *
 * It is a PURE function: no DOM, no decode, no I/O. Media is carried as a Blob
 * or URL on `RenderSource`; the worker probes real durations/sizes via mediabunny.
 *
 * ---------------------------------------------------------------------------
 * MAPPING OVERVIEW
 * ---------------------------------------------------------------------------
 * SOURCES
 *   - The main source video → one `RenderSource` (kind 'video', blob preferred,
 *     else url). It is referenced by every source-origin clip.
 *   - Each distinct timeline-clip media (asset/split/overlay clips that carry a
 *     `directUrl`) → its own `RenderSource` (deduped by url).
 *   - Each distinct soundtrack (`soundtracks[].previewUrl`) → its own audio
 *     `RenderSource` (deduped by url).
 *   - Each distinct overlay layer asset (animated/static PiP overlays) → its own
 *     `RenderSource` (image kind), referenced by a sticker.
 *
 * CLIPS (video / overlay tracks)
 *   - WITH an explicit media timeline: each `mediaTimelineClip` becomes a
 *     `RenderClip`.
 *       • `track:'primary'` → the base VIDEO track, full-frame cover.
 *       • `track:'split'`   → the base VIDEO track, but laid into the BOTTOM half
 *         of the frame (its partner primary clip, if any, fills the top half).
 *         This mirrors the legacy `drawMediaTimelineSplitClip` 2-up panel.
 *       • `track:'overlay'` → the OVERLAY track as a top-right picture-in-picture
 *         box, mirroring the legacy `drawMediaTimelineOverlayClip` placement.
 *     Each clip carries timeline `startSeconds`/`endSeconds` and, for video
 *     sources, the cut window `sourceStartSeconds`/`sourceEndSeconds` derived
 *     from `sourceOffsetSeconds` + `durationSeconds`.
 *   - WITHOUT an explicit timeline (the plain trim/clip case): one VIDEO clip on
 *     the base track covering `[start,end]` of the source, with the cut window
 *     `sourceStartSeconds=start`, `sourceEndSeconds=end`.
 *
 * AUDIO RULE (product owner, critical)
 *   The source's audio is "cut WITH the video": every source-origin VIDEO clip
 *   implicitly carries its own audio for its source window. We do NOT emit a
 *   separate stripped audio clip for it — the worker reads each video clip's
 *   source audio directly. Only ACTUAL separate soundtracks (`soundtracks[]`)
 *   become explicit `audio`-kind clips on the audio track, mapped with
 *   startTime/endTime (timeline), sourceOffset → sourceStartSeconds, volume and
 *   playbackRate.
 *
 * CAPTIONS  → `RenderCaption[]` (per-layer caption layers, else timed segments,
 *             else a single whole-render caption). Percent positions → 0..1.
 * OVERLAYS  → `RenderSticker[]` (full-frame animated/static overlay assets) +
 *             overlay-track timeline clips (PiP, handled as clips above).
 * EFFECTS / FEATURE LAYERS → `RenderEffect[]` (type = preset/kind, params carry
 *             color/opacity/intensity/text/position + the raw feature params).
 *
 * OUTPUT
 *   - width/height respect `isShort` (1080×1920 portrait vs 1920×1080 landscape;
 *     a caller-supplied natural size for landscape is honored when provided).
 *   - fps / bitrate from `outputSettings` when present, else sane defaults.
 *   - thumbnailCount passed through (clamped 1..15 like the legacy renderer).
 *
 * ---------------------------------------------------------------------------
 * ASSUMPTIONS (documented in the task report)
 * ---------------------------------------------------------------------------
 * - Split/overlay placement geometry is REPLICATED from the legacy canvas
 *   renderer (the 2-up split + top-right PiP box). The worker compositor uses
 *   the same normalized-rect + object-fit model, so these transforms reproduce
 *   the on-screen layout.
 * - Source-origin clips are assumed VIDEO (the Processing source is always a
 *   video). Asset/overlay/split clips are assumed VIDEO unless their `kind`
 *   explicitly marks an image; an `image` kind drops the source cut window.
 * - When no explicit timeline is present, `start`/`end` are taken from the
 *   caller (already clamped upstream); here we only guard ordering.
 * - `RenderSticker` here uses overlay layer opacity; entrance/exit animation
 *   (`isAnimated`) is passed as the `animation` hint for the compositor.
 */

export interface ProcessingRenderJobArgs {
  /** Stable job id; a random id is generated when omitted. */
  id?: string | null;

  /** Main source video — exactly one of blob/url should be set (url preferred fallback). */
  sourceVideoBlob?: Blob | null;
  sourceVideoUrl?: string | null;
  /** Optional natural size of the source (lets the worker lay out before decode). */
  sourceWidth?: number | null;
  sourceHeight?: number | null;
  sourceDurationSeconds?: number | null;

  /** 9:16 short vs 16:9 main. Drives output dimensions + split/PiP framing. */
  isShort?: boolean | null;

  /** Trim window of the source for the NO-explicit-timeline case (seconds). */
  startTime?: number | null;
  endTime?: number | null;

  /** Cut-parts timeline. When present, drives clips instead of [start,end]. */
  mediaTimelineClips?: ReviewBrowserMediaTimelineClip[] | null;

  /** Separate soundtrack layers → explicit audio clips. */
  soundtracks?: ReviewSoundtrackSelection[] | null;

  /** Full-frame animated/static overlay layers → stickers. */
  overlayLayers?: ReviewOverlayLayerSelection[] | null;

  /** Caption configuration (layers / segments / single). */
  captions?: ReviewBrowserRenderCaptionOptions | null;

  /** Effects / transitions / stickers / beats → RenderEffect[]. */
  featureLayers?: ReviewBrowserFeatureLayer[] | null;

  /** Color filter (warm/clean/punchy/mono) → a render-wide effect. */
  filterId?: string | null;

  /** Output overrides. */
  fps?: number | null;
  bitrate?: number | null;
  thumbnailCount?: number | null;

  /** Background fill (theme-resolved hex on the main thread). */
  backgroundColor?: string | null;
}

const DEFAULT_FPS = 30;
const DEFAULT_BITRATE = 8_000_000;
const EPSILON = 0.05;

/** Split panel geometry — mirrors `drawMediaTimelineSplitClip` (2-up with a thin gap). */
const SPLIT_GAP_FRACTION = 0.01;

/** PiP overlay box geometry — mirrors `drawMediaTimelineOverlayClip` (top-right). */
const PIP_WIDTH_FRACTION = 0.42;
const PIP_RIGHT_MARGIN_FRACTION = 0.045;
const PIP_TOP_FRACTION = 0.075;
const PIP_MAX_HEIGHT_FRACTION = 0.34;

/**
 * buildProcessingRenderJob — maps Processing's render inputs to a RenderJob.
 * Pure and deterministic (modulo the generated id).
 */
export function buildProcessingRenderJob(args: ProcessingRenderJobArgs): RenderJob {
  const isShort = !!args.isShort;
  const { width, height } = resolveDimensions(args, isShort);

  // ---- Sources ----
  const sources: RenderSource[] = [];
  const urlToSourceId = new Map<string, string>();

  const mainSourceId = 'src-main';
  sources.push({
    id: mainSourceId,
    kind: 'video',
    blob: args.sourceVideoBlob ?? null,
    url: args.sourceVideoBlob ? null : args.sourceVideoUrl ?? null,
    durationSeconds: positiveOrNull(args.sourceDurationSeconds),
    width: positiveOrNull(args.sourceWidth),
    height: positiveOrNull(args.sourceHeight)
  });

  /** Register (or reuse) a source for a given media url, returning its id. */
  const ensureSource = (url: string | null | undefined, kind: RenderSource['kind']): string | null => {
    const normalized = (url || '').trim();
    if (!normalized) return null;
    const existing = urlToSourceId.get(normalized);
    if (existing) return existing;
    const id = `src-${kind}-${sources.length}`;
    urlToSourceId.set(normalized, id);
    sources.push({ id, kind, blob: null, url: normalized });
    return id;
  };

  // ---- Tracks / clips ----
  const videoTrack: RenderTrack = { id: 'track-video', kind: 'video', clips: [] };
  const overlayTrack: RenderTrack = { id: 'track-overlay', kind: 'overlay', clips: [] };
  const audioTrack: RenderTrack = { id: 'track-audio', kind: 'audio', clips: [] };

  const timelineClips = normalizeMediaTimelineClips(args.mediaTimelineClips);
  const hasExplicitTimeline = timelineClips.length > 0;

  let timelineDurationSeconds = 0;

  if (hasExplicitTimeline) {
    // Map from a normalized primary input clip → its pushed RenderClip, so a
    // split clip can reframe its overlapping primary partner into the top half.
    const pushedPrimaryClips: Array<{ start: number; end: number; clip: RenderClip }> = [];
    let clipIndex = 0;

    for (const clip of timelineClips) {
      const start = clip.startTimeSeconds;
      const end = clip.startTimeSeconds + clip.durationSeconds;
      timelineDurationSeconds = Math.max(timelineDurationSeconds, end);

      const isSourceOrigin = (clip.origin ?? 'source') === 'source';
      const isImage = clip.kind === 'image';
      const kind: RenderClip['kind'] = isImage ? 'image' : 'video';

      const sourceId = isSourceOrigin
        ? mainSourceId
        : ensureSource(clip.directUrl, kind === 'image' ? 'image' : 'video');
      if (!sourceId) continue; // asset clip with no usable media — skip.

      const cutWindow = isImage
        ? {}
        : sourceWindow(clip, isSourceOrigin, args);

      const transform: RenderTransform | undefined = clip.track === 'split'
        ? splitBottomTransform()
        : clip.track === 'overlay'
          ? pipTransform(width, height)
          // primary → fit CENTERED (contain), so a 16:9 source in a 9:16 short
          // letterboxes with black bars instead of being cropped/zoomed.
          : { fit: 'contain' };

      const renderClip: RenderClip = {
        id: clip.id || `clip-${clipIndex++}`,
        sourceId,
        kind,
        startSeconds: round3(start),
        endSeconds: round3(end),
        ...cutWindow,
        // Source-origin video clips carry their OWN audio (cut WITH the video).
        // Asset/overlay clips default to their natural audio too; only true
        // separate soundtracks are added as explicit audio clips below.
        ...(transform ? { transform } : {})
      };

      if (clip.track === 'overlay') {
        overlayTrack.clips.push(renderClip);
      } else {
        if (clip.track === 'split') {
          // The partner primary clip (if any) is reframed into the TOP half so
          // the 2-up split matches the legacy panel layout.
          const partner = pushedPrimaryClips.find(p => start < p.end && end > p.start);
          if (partner) partner.clip.transform = splitTopTransform();
        } else if (clip.track === 'primary') {
          pushedPrimaryClips.push({ start, end, clip: renderClip });
        }
        videoTrack.clips.push(renderClip);
      }
    }
  } else {
    // No explicit timeline: one base VIDEO clip covering [start,end] of source.
    const start = Math.max(0, finiteOr(args.startTime, 0));
    const rawEnd = finiteOr(args.endTime, NaN);
    const end = Number.isFinite(rawEnd) && rawEnd > start
      ? rawEnd
      : start + Math.max(EPSILON, finiteOr(args.sourceDurationSeconds, 0) - start);
    timelineDurationSeconds = Math.max(EPSILON, end - start);

    videoTrack.clips.push({
      id: 'clip-source',
      sourceId: mainSourceId,
      kind: 'video',
      // Output timeline is rebased to 0 (the cut starts the rendered video).
      startSeconds: 0,
      endSeconds: round3(timelineDurationSeconds),
      sourceStartSeconds: round3(start),
      sourceEndSeconds: round3(end),
      // Fit centered (letterbox) so a 16:9 source in a 9:16 short keeps black bars.
      transform: { fit: 'contain' }
      // Source audio rides along with this clip (cut WITH the video).
    });
  }

  // ---- Soundtracks → explicit audio clips ----
  const soundtracks = args.soundtracks ?? [];
  let soundtrackIndex = 0;
  for (const track of soundtracks) {
    const sourceId = ensureSource(track.previewUrl, 'audio');
    if (!sourceId) continue;

    const start = Math.max(0, finiteOr(track.startTime, 0));
    const end = Number.isFinite(track.endTime ?? NaN) && (track.endTime as number) > start
      ? (track.endTime as number)
      : Math.max(start + EPSILON, timelineDurationSeconds);
    const sourceOffset = Math.max(0, finiteOr(track.sourceOffsetSeconds, 0));
    const playbackRate = positiveOr(track.playbackRate, 1);

    audioTrack.clips.push({
      id: track.id || `audio-${soundtrackIndex++}`,
      sourceId,
      kind: 'audio',
      startSeconds: round3(start),
      endSeconds: round3(end),
      sourceStartSeconds: round3(sourceOffset),
      // Map the timeline span back into the source via the playback rate.
      sourceEndSeconds: round3(sourceOffset + (end - start) * playbackRate),
      playbackRate,
      volume: clamp01(finiteOr(track.volume, 0.5))
    });
    timelineDurationSeconds = Math.max(timelineDurationSeconds, end);
  }

  // ---- Captions ----
  const captions = buildCaptions(args.captions, timelineDurationSeconds);

  // ---- Overlay layers (full-frame animated/static) → stickers ----
  const stickers = buildOverlayStickers(args.overlayLayers, ensureSource, timelineDurationSeconds);

  // ---- Feature layers + color filter → effects ----
  const effects = buildEffects(args.featureLayers, args.filterId, timelineDurationSeconds);

  const tracks: RenderTrack[] = [videoTrack, overlayTrack, audioTrack].filter(t => t.clips.length > 0);

  const timeline: RenderTimeline = {
    durationSeconds: round3(Math.max(EPSILON, timelineDurationSeconds)),
    tracks,
    ...(captions.length ? { captions } : {}),
    ...(stickers.length ? { stickers } : {}),
    ...(effects.length ? { effects } : {}),
    // Letterbox/pillarbox fill is BLACK for video (never a theme color).
    backgroundColor: args.backgroundColor || '#000000'
  };

  // Custom fonts the burned captions need (e.g. the Urdu face for ur-language
  // caption layers). Empty for Latin-only captions → no `fonts` → output unchanged.
  const fonts = fontSpecsForCaptions(captions);

  return {
    id: args.id || generateId(),
    origin: 'processing',
    sources,
    timeline,
    output: {
      width,
      height,
      fps: positiveOr(args.fps, DEFAULT_FPS),
      bitrate: positiveOr(args.bitrate, DEFAULT_BITRATE),
      videoCodec: 'avc',
      thumbnailCount: clampThumbnailCount(args.thumbnailCount),
      ...(fonts.length ? { fonts } : {})
    }
  };
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

/** Output dimensions: portrait short = 1080×1920; landscape uses natural size or 1920×1080. */
function resolveDimensions(args: ProcessingRenderJobArgs, isShort: boolean): { width: number; height: number } {
  if (isShort) return { width: 1080, height: 1920 };
  const w = positiveOrNull(args.sourceWidth);
  const h = positiveOrNull(args.sourceHeight);
  if (w && h) return { width: w, height: h };
  return { width: 1920, height: 1080 };
}

/**
 * normalizeMediaTimelineClips — mirrors the legacy normalizer: clamps durations,
 * back-fills sequential start times, defaults origin/track/sourceOffset.
 */
function normalizeMediaTimelineClips(
  clips: ReviewBrowserMediaTimelineClip[] | null | undefined
): Array<Required<Pick<ReviewBrowserMediaTimelineClip, 'startTimeSeconds' | 'durationSeconds'>> & ReviewBrowserMediaTimelineClip & { track: 'primary' | 'overlay' | 'split'; origin: 'source' | 'asset'; sourceOffsetSeconds: number }> {
  let cursor = 0;
  return (clips || []).map(clip => {
    const duration = Math.max(EPSILON, finiteOr(clip.durationSeconds, EPSILON));
    const start = Number.isFinite(clip.startTimeSeconds) && clip.startTimeSeconds >= 0
      ? clip.startTimeSeconds
      : cursor;
    cursor = Math.max(cursor, start + duration);
    const track: 'primary' | 'overlay' | 'split' =
      clip.track === 'overlay' || clip.track === 'split' ? clip.track : 'primary';
    const origin: 'source' | 'asset' = clip.origin === 'source' ? 'source' : (clip.origin ?? 'asset') as 'source' | 'asset';
    const sourceOffsetSeconds = Math.max(0, finiteOr(clip.sourceOffsetSeconds, 0));
    return { ...clip, startTimeSeconds: start, durationSeconds: duration, track, origin, sourceOffsetSeconds };
  });
}

/** Cut window into the source media for a (video) timeline clip. */
function sourceWindow(
  clip: { sourceOffsetSeconds: number; durationSeconds: number },
  isSourceOrigin: boolean,
  args: ProcessingRenderJobArgs
): { sourceStartSeconds?: number; sourceEndSeconds?: number } {
  const sourceStart = Math.max(0, clip.sourceOffsetSeconds);
  let sourceEnd = sourceStart + clip.durationSeconds;
  // Guard against running past a known source duration (source-origin clips only).
  if (isSourceOrigin) {
    const dur = positiveOrNull(args.sourceDurationSeconds);
    if (dur && sourceEnd > dur) sourceEnd = dur;
  }
  return { sourceStartSeconds: round3(sourceStart), sourceEndSeconds: round3(sourceEnd) };
}

/** Top half of the 2-up split (matches legacy panelHeight = floor((h - gap)/2)). */
function splitTopTransform(): RenderTransform {
  const half = (1 - SPLIT_GAP_FRACTION) / 2;
  return { x: 0, y: 0, width: 1, height: round3(half), fit: 'cover' };
}

/** Bottom half of the 2-up split, offset below the gap. */
function splitBottomTransform(): RenderTransform {
  const half = (1 - SPLIT_GAP_FRACTION) / 2;
  const top = round3(half + SPLIT_GAP_FRACTION);
  return { x: 0, y: top, width: 1, height: round3(1 - top), fit: 'cover' };
}

/** Top-right picture-in-picture box (matches legacy drawMediaTimelineOverlayClip). */
function pipTransform(width: number, height: number): RenderTransform {
  const boxW = width * PIP_WIDTH_FRACTION;
  const boxH = Math.min(height * PIP_MAX_HEIGHT_FRACTION, boxW * (height / Math.max(1, width)));
  const x = (width - boxW - width * PIP_RIGHT_MARGIN_FRACTION) / width;
  const y = PIP_TOP_FRACTION;
  return {
    x: round3(x),
    y: round3(y),
    width: round3(PIP_WIDTH_FRACTION),
    height: round3(boxH / height),
    fit: 'contain'
  };
}

/** Captions → RenderCaption[]: per-layer > timed segments > single whole-render. */
function buildCaptions(
  captions: ReviewBrowserRenderCaptionOptions | null | undefined,
  durationSeconds: number
): RenderCaption[] {
  if (!captions || !(captions.enabled ?? false)) return [];

  const out: RenderCaption[] = [];

  // 1. Explicit caption layers (each with its own timing/placement).
  const layers = captions.layers || [];
  if (layers.length) {
    layers.forEach((layer, index) => {
      const text = (layer.text || '').trim();
      if (!text) return;
      out.push({
        id: layer.id || `caption-layer-${index}`,
        text,
        startSeconds: round3(Math.max(0, finiteOr(layer.startTimeSeconds, 0))),
        endSeconds: round3(finiteOr(layer.endTimeSeconds, durationSeconds)),
        x: percentToFraction(layer.xPercent, captions.xPercent, 50),
        y: captionY(captions.position, layer.yPercent ?? captions.yPercent),
        fontFamily: layer.language === 'ur' ? 'urdu' : undefined,
        color: layer.color || undefined,
        bold: true,
        align: 'center'
      });
    });
    if (out.length) return out;
  }

  // 2. Timed transcript segments.
  const segments: ReviewCaptionSegment[] = captions.segments || [];
  if (segments.length) {
    segments.forEach((segment, index) => {
      const text = (segment.text || '').trim();
      if (!text) return;
      out.push({
        id: `caption-seg-${index}`,
        text,
        startSeconds: round3(Math.max(0, finiteOr(segment.start, 0))),
        endSeconds: round3(finiteOr(segment.end, durationSeconds)),
        x: percentToFraction(undefined, captions.xPercent, 50),
        y: captionY(captions.position, captions.yPercent),
        bold: true,
        align: 'center'
      });
    });
    return out;
  }

  // 3. Single whole-render caption from the transcript text.
  const text = (captions.text || '').trim();
  if (text) {
    out.push({
      id: 'caption-main',
      text,
      startSeconds: 0,
      endSeconds: round3(durationSeconds),
      x: percentToFraction(undefined, captions.xPercent, 50),
      y: captionY(captions.position, captions.yPercent),
      bold: true,
      align: 'center'
    });
  }
  return out;
}

/** Overlay layers (full-frame animated/static assets) → stickers. */
function buildOverlayStickers(
  overlayLayers: ReviewOverlayLayerSelection[] | null | undefined,
  ensureSource: (url: string | null | undefined, kind: RenderSource['kind']) => string | null,
  durationSeconds: number
) {
  const stickers: NonNullable<RenderTimeline['stickers']> = [];
  (overlayLayers || []).forEach((layer, index) => {
    const asset = layer.asset;
    if (!asset) return;
    const url = asset.url || asset.previewUrl || asset.dataUrl || asset.ref || null;
    const sourceId = ensureSource(url, 'image');
    if (!sourceId) return;
    stickers.push({
      id: layer.id || `overlay-${index}`,
      sourceId,
      startSeconds: round3(Math.max(0, finiteOr(layer.startTimeSeconds, 0))),
      endSeconds: round3(finiteOr(layer.endTimeSeconds, durationSeconds)),
      // Full-frame cover (legacy drawOverlayLayers blits 0,0,width,height).
      transform: { x: 0, y: 0, width: 1, height: 1, opacity: clamp01(finiteOr(layer.opacity, 1)), fit: 'stretch' },
      animation: asset.isAnimated ? 'overlay' : null
    });
  });
  return stickers;
}

/** Feature layers (effect/transition/sticker/beat) + color filter → RenderEffect[]. */
function buildEffects(
  featureLayers: ReviewBrowserFeatureLayer[] | null | undefined,
  filterId: string | null | undefined,
  durationSeconds: number
): RenderEffect[] {
  const effects: RenderEffect[] = [];

  // Render-wide color filter → a single effect spanning the whole timeline.
  const filter = (filterId || '').trim();
  if (filter && filter !== 'none') {
    effects.push({
      id: 'effect-filter',
      type: 'color-filter',
      startSeconds: 0,
      endSeconds: round3(durationSeconds),
      params: { filterId: filter }
    });
  }

  (featureLayers || []).forEach((layer, index) => {
    const kind = layer.kind;
    if (kind !== 'effect' && kind !== 'transition' && kind !== 'sticker' && kind !== 'beat') return;
    const start = Math.max(0, finiteOr(layer.startTimeSeconds, 0));
    const end = Number.isFinite(layer.endTimeSeconds ?? NaN) && (layer.endTimeSeconds as number) > start
      ? (layer.endTimeSeconds as number)
      : start + 0.5;

    // Pull through optional structured params if present.
    const rawParams = (layer as ReviewBrowserFeatureLayer & { params?: Record<string, unknown> | null }).params;
    const params: Record<string, number | string | boolean | null> = {
      kind,
      preset: layer.preset ?? null,
      color: layer.color ?? null,
      text: layer.text ?? null,
      xPercent: numOrNull(layer.xPercent),
      yPercent: numOrNull(layer.yPercent),
      intensity: numOrNull(layer.intensity)
    };
    if (rawParams && typeof rawParams === 'object') {
      for (const [key, value] of Object.entries(rawParams)) {
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null) {
          params[`param_${key}`] = value;
        }
      }
    }

    effects.push({
      id: layer.id || `effect-${index}`,
      type: layer.preset || kind,
      startSeconds: round3(start),
      endSeconds: round3(end),
      params
    });
  });

  return effects;
}

// ---------------------------------------------------------------------------
// Small numeric helpers (kept local + pure)
// ---------------------------------------------------------------------------

function captionY(position: string | null | undefined, yPercent: number | null | undefined): number {
  if (typeof yPercent === 'number' && Number.isFinite(yPercent)) {
    return clamp01(yPercent / 100);
  }
  if (position === 'top') return 0.18;
  if (position === 'center') return 0.5;
  return 0.82;
}

function percentToFraction(primary: number | null | undefined, fallback: number | null | undefined, defaultPercent: number): number {
  const value = typeof primary === 'number' && Number.isFinite(primary)
    ? primary
    : typeof fallback === 'number' && Number.isFinite(fallback)
      ? fallback
      : defaultPercent;
  return clamp01(value / 100);
}

function clampThumbnailCount(count: number | null | undefined): number {
  return Math.max(1, Math.min(15, Math.trunc(Number(count ?? 1) || 1)));
}

function finiteOr(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function positiveOr(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;
}

function positiveOrNull(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

function numOrNull(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function generateId(): string {
  return globalThis.crypto?.randomUUID?.() || `processing-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
